import {DateTime, Duration, Interval, IntervalMaybeValid} from 'luxon';
import {Date as SugarDate} from 'sugar';

import {maybeConvertLegacyTimezone} from '../../../libs/shared/core/src/helpers/legacyTimezonesAliases';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {DoNotDisturb} from '../../models/api/preferencesModel';

/*
 * Constants.
 */

const oneDay = Duration.fromObject({days: 1});

/*
 * Read a DnD value.
 */

let didReportError = false;

/** Returns the currently active or next DnD interval, or undefined in case of a parsing error. */
export function tryFindDoNotDisturbInterval(
  now: DateTime<true>,
  dnd: DoNotDisturb,
): IntervalMaybeValid | undefined {
  try {
    return findDoNotDisturbInterval(now, dnd);
  } catch (error) {
    if (!didReportError) {
      reportToBugsnag(error);
      didReportError = true;
    }

    return undefined;
  }
}

/** Returns the currently active or next DnD interval. */
export function findDoNotDisturbInterval(now: DateTime<true>, dnd: DoNotDisturb): IntervalMaybeValid {
  let start = parseTime(now, dnd.startTime, dnd.timezone);
  let end = parseTime(now, dnd.endTime, dnd.timezone);

  // Change the order if it starts at night and ends in the morning.
  if (start > end) {
    start = start.minus(oneDay);
  }

  // Shift for the current time of day, if necessary.
  if (now >= end) {
    start = start.plus(oneDay);
    end = end.plus(oneDay);
  }

  return Interval.fromDateTimes(start, end);
}

function parseTime(now: DateTime, time: string, timezone: string): DateTime {
  // Parse the time regardless of the timezone. We use `sugar` because it handles edge cases like 24:15.
  const date = SugarDate.create(time, {fromUTC: true});

  // Interpret that time relative to the date and time zone that were provided, since the current date and
  // timezone could theoretically be in a different Daylight Savings.
  return now
    .setZone(maybeConvertLegacyTimezone(timezone))
    .set({hour: date.getUTCHours(), minute: date.getUTCMinutes(), second: 0, millisecond: 0})
    .setZone(now.zone);
}

/*
 * Update a DnD value.
 */

export function setDoNotDisturbStart(dnd: DoNotDisturb, time: DateTime<true>): DoNotDisturb {
  const timezone = time.zoneName;
  const interval = findDoNotDisturbInterval(time, dnd);

  if (!interval.isValid) {
    return dnd;
  }

  return {
    ...dnd,
    startTime: formatTime(time, timezone),
    endTime: formatTime(interval.end, timezone),
    timezone,
  };
}

export function setDoNotDisturbEnd(dnd: DoNotDisturb, time: DateTime<true>): DoNotDisturb {
  const timezone = time.zoneName;
  const interval = findDoNotDisturbInterval(time, dnd);

  if (!interval.isValid) {
    return dnd;
  }

  return {
    ...dnd,
    startTime: formatTime(interval.start, timezone),
    endTime: formatTime(time, timezone),
    timezone,
  };
}

function formatTime(time: DateTime, timezone: string): string {
  return time.setZone(timezone).toFormat('HH:mm');
}
